/**
 * Nether mobs, round 2 (vanilla Java, normal difficulty):
 *  - ghast: 10 HP, 4x4 flier, wanders, shoots a large fireball every ~3 s at a
 *    visible player within 64 (mouth opens 10 ticks before). Fire immune.
 *    Drops 0-1 ghast tear, 0-2 gunpowder.
 *  - blaze: 20 HP, hovers (slow fall, rises toward a player above it), melee 6
 *    when adjacent, else volleys of 3 small fireballs (6 ticks apart, 100-tick
 *    rest) within 48. Fire immune. Drops 0-1 blaze rod (player kills).
 *  - wither skeleton: 20 HP, 0.7x2.4, stone sword 8. Fire immune. Wither
 *    effect not applied (no effect system). Drops 0-1 coal, 0-2 bones.
 *  - hoglin: 40 HP, 1.4x1.4, charge 3-8 with a strong upward toss, flees
 *    warped fungus within 7 blocks. Drops 2-4 porkchop, 0-1 leather.
 *  - strider: 20 HP, passive, stands on lava, "cold" (purple, shivering) off
 *    lava. Fire immune. Drops 2-5 string.
 *  - enderman: 40 HP, 0.6x2.9, 7 damage, neutral; angers when the player
 *    looks at it (main.ts sets `stared`) or hits it; teleports when hurt by
 *    anything but melee half the time, and when in water.
 */

import {
  AIR, BLOCKS, CRIMSON_FUNGUS_ID, CRIMSON_NYLIUM_ID, CRIMSON_ROOTS_ID, IS_LAVA, IS_SOLID, LAVA_ID, NETHERRACK_ID, SOUL_SAND_ID,
  SOUL_SOIL_ID, WARPED_FUNGUS_ID, WARPED_NYLIUM_ID, WARPED_ROOTS_ID, WATER_AMOUNT, WITHER_SKULL_ID,
} from '../../blocks';

/** vanilla #enderman_holdable (the blocks this engine has). */
const HOLDABLE = new Set<number>([
  ...['Grass Block', 'Dirt', 'Coarse Dirt', 'Podzol', 'Mycelium', 'Rooted Dirt', 'Sand', 'Red Sand', 'Gravel', 'Clay',
    'Pumpkin', 'Melon', 'TNT', 'Poppy', 'Dandelion', 'Red Mushroom', 'Brown Mushroom', 'Moss Block', 'Mud']
    .map((n) => BLOCKS.findIndex((b) => b?.name === n)).filter((i) => i > 0),
  NETHERRACK_ID, CRIMSON_NYLIUM_ID, WARPED_NYLIUM_ID, SOUL_SAND_ID, SOUL_SOIL_ID,
  CRIMSON_FUNGUS_ID, WARPED_FUNGUS_ID, CRIMSON_ROOTS_ID, WARPED_ROOTS_ID,
]);
import { I } from '../../game/itemIds';
import { stackOf, type Stack } from '../../game/types';
import { Mob, type MobCtx } from '../mob';
import { netherHooks } from './mobs';

const roll = (rnd: () => number, lo: number, hi: number): number => lo + Math.floor(rnd() * (hi - lo + 1));

/** Shared: fire immunity, target memory, melee. */
import { newSteer, steerFlying } from '../nav/navigator';
const FLY_STEER = newSteer();

abstract class Base extends Mob {
  fireImmune = false;
  protected seeMemory = 0;
  protected attackCd = 0;
  private ticking = false;

  override tick(ctx: MobCtx): void {
    if (this.fireImmune) this.fireTicks = 0;
    this.ticking = true;
    try { super.tick(ctx); } finally { this.ticking = false; }
    if (this.fireImmune) this.fireTicks = 0;
  }

  override hurt(amount: number): boolean {
    if (this.fireImmune && this.ticking && (this.inLava || this.fireTicks > 0)) { this.fireTicks = 0; return false; }
    return super.hurt(amount);
  }

  override onHurtBy(): void { this.retaliate = 200; }
  xpValue(_rnd?: () => number): number { return 5; }

  protected sees(ctx: MobCtx, range: number): boolean {
    const p = ctx.host.player;
    if (!ctx.targetable()) { this.seeMemory = 0; return false; }
    const dx = p.x - this.x, dy = p.y - this.y, dz = p.z - this.z;
    if (dx * dx + dy * dy + dz * dz > range * range) { this.seeMemory = 0; return false; }
    if (this.phase(ctx, 5) && ctx.los(this.x, this.eyeY, this.z, p.x, p.eyeY, p.z)) this.seeMemory = 60;
    if (this.seeMemory > 0) this.seeMemory--;
    if (this.retaliate > 0) this.seeMemory = Math.max(this.seeMemory, 1);
    return this.seeMemory > 0;
  }

  protected chaseAndHit(ctx: MobCtx, damage: number, mod: number, knock = 0.4): void {
    const p = ctx.host.player;
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    this.steerTo(ctx, p.x, p.y, p.z, mod, 6);
    if (this.attackCd === 0 && this.inMeleeReach(ctx)) {
      this.attackCd = 20;
      this.swing();
      ctx.hurtPlayer(this, damage, knock);
    }
  }

  /** Fly with velocity (vx, vy, vz): cancels the base physics' gravity for this tick. */
  protected fly(vx: number, vy: number, vz: number, k = 0.1): void {
    this.vx += (vx - this.vx) * k;
    this.vz += (vz - this.vz) * k;
    this.vy += (vy - this.vy) * k;
    // Mob.travel moves by vy, then subtracts gravity: pre-compensate so it hovers
    this.vy += 0.08 * 0.98;
  }
}

/** Shoot hook main.ts installs (fireball system lives outside the mob list). */
export const fireballHook: {
  shoot: ((small: boolean, x: number, y: number, z: number, dx: number, dy: number, dz: number, owner: number) => void) | null;
} = { shoot: null };

// --------------------------------------------------------------------- ghast

export class Ghast extends Base {
  private wx = 0; private wy = 0; private wz = 0; private wanderT = 0;
  private charge = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'ghast', x, y, z, { width: 4, height: 4, eye: 2.6, health: 10, speed: 0.1 }, rnd);
    this.fireImmune = true;
    this.noFallDamage = true;
    this.wx = x; this.wy = y; this.wz = z;
  }

  protected override floats(): boolean { return false; }

  protected ai(ctx: MobCtx): void {
    const r = ctx.host.rnd;
    // wander: a new point within 16 when arrived / stuck / every few seconds
    const dx = this.wx - this.x, dy = this.wy - this.y, dz = this.wz - this.z;
    const d = Math.hypot(dx, dy, dz);
    if (--this.wanderT <= 0 || d < 1 || this.collidedH) {
      this.wanderT = 60 + Math.floor(r() * 80);
      this.wx = this.x + (r() * 2 - 1) * 16; this.wy = Math.min(115, Math.max(35, this.y + (r() * 2 - 1) * 16)); this.wz = this.z + (r() * 2 - 1) * 16;
    }
    const sp = 0.06;
    if (d > 0.5) {
      // flyer variant: bend around blocks on the way to the wander point
      steerFlying(this.w, this.x, this.y, this.z, this.wx, this.wy, this.wz, 6, FLY_STEER);
      this.fly(FLY_STEER.dx * sp, FLY_STEER.dy * sp, FLY_STEER.dz * sp);
    } else this.fly(0, 0, 0);
    const p = ctx.host.player;
    if (this.sees(ctx, 64) && this.seeMemory >= 55) {
      this.faceToward(p.x, p.z);
      this.charge++;
      if (this.charge === 10) this.aggressive = true; // mouth opens
      if (this.charge >= 20) {
        const yaw = Math.atan2(p.z - this.z, p.x - this.x);
        const sx = this.x + Math.cos(yaw) * 2, sz = this.z + Math.sin(yaw) * 2, sy = this.y + 2;
        fireballHook.shoot?.(false, sx, sy, sz, p.x - sx, p.y + p.height / 2 - sy, p.z - sz, this.id);
        this.charge = -40;
      }
    } else if (this.charge > 0) this.charge--;
    this.aggressive = this.charge > 10;
    if (d > 0.5) this.faceToward(this.wx, this.wz);
  }

  loot(rnd: () => number): Stack[] {
    const out: Stack[] = [];
    const t = roll(rnd, 0, 1); if (t) out.push(stackOf(I.GHAST_TEAR, t));
    const g = roll(rnd, 0, 2); if (g) out.push(stackOf(I.GUNPOWDER, g));
    return out;
  }
}

// --------------------------------------------------------------------- blaze

export class Blaze extends Base {
  private volley = 0;
  private shotCd = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'blaze', x, y, z, { width: 0.6, height: 1.8, eye: 1.53, health: 20, speed: 0.23 }, rnd);
    this.fireImmune = true;
    this.noFallDamage = true;
    this.slowFall = true;
  }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.shotCd > 0) this.shotCd--;
    const p = ctx.host.player;
    if (!this.sees(ctx, 48)) {
      this.stroll(ctx, 0.6, 3, true);
      if (this.age % 40 < 20 && !this.onGround) this.vy = Math.max(this.vy, 0);
      return;
    }
    this.aggressive = true;
    this.faceToward(p.x, p.z);
    this.lookAt(p.x, p.eyeY, p.z);
    // hover up toward a player standing above
    if (p.eyeY > this.eyeY + 0.5) this.vy += (0.3 - this.vy) * 0.3;
    const dx = p.x - this.x, dz = p.z - this.z;
    const d2 = dx * dx + dz * dz;
    if (d2 < 4) { this.chaseAndHit(ctx, 6, 1); return; }
    if (this.shotCd === 0) {
      this.volley++;
      if (this.volley > 1 && this.volley <= 4) {
        // vanilla: 3 shots 6 ticks apart after a 60-tick wind-up
        fireballHook.shoot?.(true, this.x, this.y + this.height / 2 + 0.5, this.z,
          p.x - this.x, p.y + p.height / 2 - (this.y + this.height / 2), p.z - this.z, this.id);
        this.shotCd = 6;
      } else if (this.volley > 4) { this.volley = 0; this.shotCd = 100; }
      else this.shotCd = 60;
    }
    if (d2 > 100) this.setMove(dx, dz, 0.5);
  }

  loot(rnd: () => number, byPlayer: boolean): Stack[] {
    return byPlayer && rnd() < 0.5 ? [stackOf(I.BLAZE_ROD, 1)] : [];
  }
  override xpValue(): number { return 10; }
}

// ---------------------------------------------------------- wither skeleton

export class WitherSkeleton extends Base {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'wither_skeleton', x, y, z, { width: 0.7, height: 2.4, eye: 2.1, health: 20, speed: 0.25 }, rnd);
    this.fireImmune = true;
    this.heldItem = I.STONE_SWORD;
  }
  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.sees(ctx, 16)) this.chaseAndHit(ctx, 8, 1.25);
    else { this.stroll(ctx, 0.8, 4, false); this.lookAtNearPlayer(ctx); }
  }
  loot(rnd: () => number, byPlayer = false): Stack[] {
    const out: Stack[] = [];
    const c = roll(rnd, 0, 1); if (c) out.push(stackOf(I.COAL, c));
    const b = roll(rnd, 0, 2); if (b) out.push(stackOf(I.BONE, b));
    if (byPlayer && rnd() < 0.025) out.push(stackOf(WITHER_SKULL_ID, 1)); // vanilla 2.5% skull when a player kills it
    return out;
  }
}

// -------------------------------------------------------------------- hoglin

export class Hoglin extends Base {
  private fleeT = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'hoglin', x, y, z, { width: 1.3965, height: 1.4, eye: 0.9, health: 40, speed: 0.3 }, rnd);
  }

  private fungusNear(): { x: number; z: number } | null {
    const bx = Math.floor(this.x), by = Math.floor(this.y), bz = Math.floor(this.z);
    for (let dy = -2; dy <= 2; dy++) for (let dz = -7; dz <= 7; dz++) for (let dx = -7; dx <= 7; dx++) {
      if (this.w.getBlock(bx + dx, by + dy, bz + dz) === WARPED_FUNGUS_ID) return { x: bx + dx, z: bz + dz };
    }
    return null;
  }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.phase(ctx, 20)) {
      const f = this.fungusNear();
      if (f) { this.fleeT = 40; this.faceToward(this.x * 2 - f.x, this.z * 2 - f.z); this.setMove(this.x - f.x, this.z - f.z, 1.2); }
    }
    if (this.fleeT > 0) { this.fleeT--; return; }
    if (this.sees(ctx, 16)) {
      const dmg = roll(ctx.host.rnd, 3, 8);
      const before = this.attackCd;
      this.chaseAndHit(ctx, dmg, 1, 0.6);
      // vanilla hoglins toss their target upward
      if (before === 0 && this.attackCd === 20) netherHooks.toss?.(0.5);
    } else this.stroll(ctx, 0.6, 4, false);
  }

  loot(rnd: () => number): Stack[] {
    const out = [stackOf(I.PORKCHOP, roll(rnd, 2, 4))];
    const l = roll(rnd, 0, 1); if (l) out.push(stackOf(I.LEATHER, l));
    return out;
  }
}

// ------------------------------------------------------------------- strider

/** How far a strider's feet sit below the top of the lava block it wades on. */
const STRIDER_WADE = 0.06;

export class Strider extends Base {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'strider', x, y, z, { width: 0.9, height: 1.7, eye: 1.4, health: 20, speed: 0.175 }, rnd);
    this.fireImmune = true;
    this.lavaWalker = true;
  }

  protected override floats(): boolean { return false; }

  /** Standing on / in lava (warm); off lava it is cold: purple, shivering and slow. */
  get onLava(): boolean { return this.color === 0; }

  /**
   * Walk on the lava surface and keep the cold state; water and rain hurt it
   * (vanilla isSensitiveToWater). Shared by `ai()` and the ridden controller.
   */
  surface(ctx: MobCtx): boolean {
    const bx = Math.floor(this.x), bz = Math.floor(this.z);
    const w = this.w;
    // the lava cell whose surface we wade on: lava with no lava above it
    const hi = Math.floor(this.y + 0.5);
    let cell = -1;
    for (let k = hi; k >= hi - 1; k--) {
      if (IS_LAVA[w.getBlock(bx, k, bz)] === 1 && IS_LAVA[w.getBlock(bx, k + 1, bz)] !== 1) { cell = k; break; }
    }
    let lava = false;
    if (cell >= 0) {
      // feet sit just under the surface; held there like ground so it wades
      // at walking speed instead of bobbing through swim physics
      const top = cell + 1 - STRIDER_WADE;
      if (this.y <= top + 0.05) {
        this.y = Math.min(top, this.y + 0.15);
        this.vy = 0;
        this.onGround = true;
      }
      lava = this.y <= top + 0.5;
    } else if (IS_LAVA[w.getBlock(bx, Math.floor(this.y), bz)] === 1) {
      this.vy = 0.12; // submerged: rise to the surface
      lava = true;
    }
    this.color = lava ? 0 : 1; // 1 = cold (purple, shivering)
    if (this.inWater || this.rainingHere(ctx)) this.hurt(1);
    return lava;
  }

  protected ai(ctx: MobCtx): void {
    const lava = this.surface(ctx);
    this.stroll(ctx, lava ? 1 : 0.5, 3, true);
    this.lookAtNearPlayer(ctx);
  }

  loot(rnd: () => number): Stack[] { return [stackOf(I.STRING, roll(rnd, 2, 5))]; }
  override xpValue(rnd: () => number): number { return 1 + Math.floor(rnd() * 3); }
}

// ------------------------------------------------------------------ enderman

export class Enderman extends Base {
  /** Set by main.ts each tick while the player's crosshair rests on it. */
  stared = false;
  private anger = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'enderman', x, y, z, { width: 0.6, height: 2.9, eye: 2.55, health: 40, speed: 0.3 }, rnd);
  }

  /** Block id carried in its hands (0 = none), vanilla carriedBlock. */
  carried = 0;
  override onHurtBy(): void { this.anger = 600; this.retaliate = 200; }
  saveExtra(): unknown { return [this.anger, this.carried]; }
  loadExtra(d: unknown): void {
    const a = d as number[];
    this.anger = typeof a?.[0] === 'number' ? a[0] : 0;
    this.carried = typeof a?.[1] === 'number' ? a[1] : 0;
  }

  /** vanilla EndermanTakeBlockGoal / EndermanLeaveBlockGoal (1/20 and 1/2000 per tick). */
  private carry(rnd: () => number): void {
    const set = netherHooks.setBlock;
    if (!set) return;
    const x = Math.floor(this.x + (rnd() * 2 - 1) * 2), y = Math.floor(this.y + rnd() * 3), z = Math.floor(this.z + (rnd() * 2 - 1) * 2);
    if (this.carried === 0) {
      if (rnd() >= 1 / 20) return;
      const id = this.w.getBlock(x, y, z);
      if (HOLDABLE.has(id) && set(x, y, z, AIR)) this.carried = id;
    } else {
      if (rnd() >= 1 / 2000) return;
      if (this.w.getBlock(x, y, z) === AIR && IS_SOLID[this.w.getBlock(x, y - 1, z)] === 1 && set(x, y, z, this.carried)) this.carried = 0;
    }
  }

  /** Arrows / thrown projectiles never hurt an enderman: it teleports away instead (manager.ts arrowCtx). */
  dodgeProjectile(): boolean {
    if (this.dying) return false;
    this.teleportRandom(this.rng);
    return true;
  }

  override hurt(amount: number): boolean {
    const r = super.hurt(amount);
    if (r && !this.dying && this.rng() < 0.5) this.teleportRandom(this.rng);
    return r;
  }

  private teleportRandom(rnd: () => number): boolean {
    if (!this.w) return false;
    for (let i = 0; i < 16; i++) {
      const tx = Math.floor(this.x + (rnd() - 0.5) * 64), tz = Math.floor(this.z + (rnd() - 0.5) * 64);
      let ty = Math.floor(this.y + (rnd() - 0.5) * 64);
      for (let k = 0; k < 64 && ty > 1; k++, ty--) {
        const b = this.w.getBlock(tx, ty - 1, tz);
        if (IS_SOLID[b] === 1 && b !== LAVA_ID && this.w.getBlock(tx, ty, tz) === AIR
          && this.w.getBlock(tx, ty + 1, tz) === AIR && this.w.getBlock(tx, ty + 2, tz) === AIR) {
          this.x = this.px = tx + 0.5; this.y = this.py = ty; this.z = this.pz = tz + 0.5;
          this.vx = this.vy = this.vz = 0;
          netherHooks.effect?.('portal', this.x, this.y + 1.4, this.z);
          return true;
        }
      }
    }
    return false;
  }

  protected ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.inWater || WATER_AMOUNT[this.w.getBlock(Math.floor(this.x), Math.floor(this.eyeY), Math.floor(this.z))] > 0) {
      // vanilla: water hurts (1 per hit, invulnerability-gated) and makes it teleport away
      this.hurt(1);
      this.teleportRandom(ctx.host.rnd);
    }
    if (this.anger === 0) this.carry(ctx.host.rnd);
    if (this.stared && ctx.targetable()) this.anger = Math.max(this.anger, 600);
    this.stared = false;
    if (this.anger > 0) this.anger--;
    if (this.anger > 0 && this.sees(ctx, 64)) this.chaseAndHit(ctx, 7, 1.4);
    else { this.stroll(ctx, 0.6, 4, true); this.lookAtNearPlayer(ctx); }
    this.aggressive = this.anger > 0;
  }

  loot(rnd: () => number): Stack[] {
    const out: Stack[] = [];
    if (rnd() < 0.5) out.push(stackOf(I.ENDER_PEARL, 1)); // vanilla 0-1
    if (this.carried) out.push(stackOf(this.carried, 1)); // drops what it held
    return out;
  }
  override syncView(host: Parameters<Mob['syncView']>[0]): void {
    super.syncView(host);
    this.view.color = this.carried; // the renderer draws the carried block
  }
}
